import React, { useMemo, useState } from "react";
import CognitiveRadarCompareChart from "./CognitiveRadarCompareChart";
import SocialCognitionScatterChart from "./SocialCognitionScatterChart";
import BeckAffectiveSpectrumChart from "./BeckAffectiveSpectrumChart";
import CriticalItemsBreakdownChart from "./CriticalItemsBreakdownChart";
import { buildCognitiveRadarProfile, countRadarDataPoints } from "../utils/patientCognitiveRadar";
import { buildSocialCognitionScatter } from "../utils/patientSocialCognitionScatter";
import { buildBeckAffectiveSpectrum } from "../utils/patientBeckAffectiveSpectrum";
import { buildCriticalItemsBreakdown } from "../utils/patientCriticalItems";

function PatientSummaryCard({ patient, label }) {
  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("es-MX", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  return (
    <article className="expedientes-compare-card">
      <div className="expedientes-compare-card-head">
        <span className="expedientes-compare-avatar" aria-hidden="true">
          {patient.gender?.toLowerCase().startsWith("f") ? "♀" : "♂"}
        </span>
        <div>
          <p className="expedientes-compare-card-label">{label}</p>
          <h3 className="expedientes-compare-card-name">{patient.name}</h3>
        </div>
      </div>
      <div className="expedientes-compare-card-info">
        <p><strong>Edad:</strong> {patient.age} años</p>
        <p><strong>Fecha de nacimiento:</strong> {formatDate(patient.birthDate)}</p>
        <p><strong>Sexo:</strong> {patient.gender}</p>
        <p><strong>ID:</strong> {patient.idhub || patient.id || "—"}</p>
        <p><strong>Años de escolaridad:</strong> {patient.escolaridad ?? "—"}</p>
        <p><strong>Lateralidad:</strong> {patient.lateralidad ?? "—"}</p>
      </div>
    </article>
  );
}

export default function ExpedientesCompareView({
  patientA,
  patientB,
  onClose,
  groupTestsByName,
  TestAccordion,
}) {
  const [activeTab, setActiveTab] = useState("dashboard");

  const profileA = useMemo(
    () => buildCognitiveRadarProfile(patientA?.tests || []),
    [patientA]
  );
  const profileB = useMemo(
    () => buildCognitiveRadarProfile(patientB?.tests || []),
    [patientB]
  );
  const scatterA = useMemo(
    () => buildSocialCognitionScatter(patientA?.tests || []),
    [patientA]
  );
  const scatterB = useMemo(
    () => buildSocialCognitionScatter(patientB?.tests || []),
    [patientB]
  );
  const beckA = useMemo(
    () => buildBeckAffectiveSpectrum(patientA?.tests || []),
    [patientA]
  );
  const beckB = useMemo(
    () => buildBeckAffectiveSpectrum(patientB?.tests || []),
    [patientB]
  );
  const itemsA = useMemo(
    () => buildCriticalItemsBreakdown(patientA?.tests || []),
    [patientA]
  );
  const itemsB = useMemo(
    () => buildCriticalItemsBreakdown(patientB?.tests || []),
    [patientB]
  );

  const hasRadarData =
    countRadarDataPoints(profileA) > 0 || countRadarDataPoints(profileB) > 0;

  return (
    <div className="expedientes-compare-panel">
      <header className="expedientes-compare-header">
        <h2 className="expedientes-compare-title">Comparación de Expedientes</h2>
        <button type="button" className="expedientes-compare-close" onClick={onClose}>
          Salir de comparación
        </button>
      </header>

      <div className="expedientes-compare-cards">
        <PatientSummaryCard patient={patientA} label="Paciente A" />
        <PatientSummaryCard patient={patientB} label="Paciente B" />
      </div>

      <div className="expedientes-compare-tabs-row">
        <div className="expedientes-detail-tabs expedientes-compare-tabs">
          <button
            type="button"
            className={`expedientes-tab ${activeTab === "administradas" ? "expedientes-tab--active" : ""}`}
            onClick={() => setActiveTab("administradas")}
          >
            Pruebas administradas
          </button>
          <button
            type="button"
            className={`expedientes-tab ${activeTab === "por-aplicar" ? "expedientes-tab--active" : ""}`}
            onClick={() => setActiveTab("por-aplicar")}
          >
            Pruebas por aplicar
          </button>
          <button
            type="button"
            className={`expedientes-tab ${activeTab === "dashboard" ? "expedientes-tab--active" : ""}`}
            onClick={() => setActiveTab("dashboard")}
          >
            Dashboard único
          </button>
        </div>
      </div>

      {activeTab === "administradas" && (
        <div className="expedientes-compare-split">
          <div>
            <h4 className="expedientes-compare-section-label">{patientA.name}</h4>
            <div className="expedientes-tests-accordions">
              {groupTestsByName(patientA.tests || []).map((group, idx) => (
                <TestAccordion key={group.testName} group={group} defaultOpen={idx === 0} />
              ))}
            </div>
          </div>
          <div>
            <h4 className="expedientes-compare-section-label">{patientB.name}</h4>
            <div className="expedientes-tests-accordions">
              {groupTestsByName(patientB.tests || []).map((group, idx) => (
                <TestAccordion key={group.testName} group={group} defaultOpen={idx === 0} />
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "por-aplicar" && (
        <p className="expedientes-por-aplicar-placeholder">
          Listado de pruebas por aplicar (en desarrollo).
        </p>
      )}

      {activeTab === "dashboard" && (
        <div className="expedientes-compare-dashboard">
          <header className="expedientes-dashboard-header">
            <h3 className="expedientes-dashboard-title">
              Radar de perfil cognitivo-emocional integrado
            </h3>
            <p className="expedientes-dashboard-subtitle">
              Comparación del porcentaje alcanzado en la última sesión de cada prueba.
            </p>
          </header>

          {!hasRadarData ? (
            <p className="expedientes-dashboard-empty">
              Los pacientes seleccionados no tienen datos suficientes para el radar.
            </p>
          ) : (
            <CognitiveRadarCompareChart
              profileA={profileA}
              profileB={profileB}
              patientNameA={patientA.name}
              patientNameB={patientB.name}
            />
          )}

          <section className="expedientes-dashboard-block">
            <header className="expedientes-dashboard-header">
              <h3 className="expedientes-dashboard-title">
                Módulo de cognición social: empatía vs. decodificación expresiva
              </h3>
            </header>
            <div className="expedientes-compare-split">
              <div>
                <h4 className="expedientes-compare-section-label">{patientA.name}</h4>
                <SocialCognitionScatterChart scatter={scatterA} />
              </div>
              <div>
                <h4 className="expedientes-compare-section-label">{patientB.name}</h4>
                <SocialCognitionScatterChart scatter={scatterB} />
              </div>
            </div>
          </section>

          <section className="expedientes-dashboard-block">
            <header className="expedientes-dashboard-header">
              <h3 className="expedientes-dashboard-title">
                Espectro afectivo: comparativa Beck
              </h3>
            </header>
            <div className="expedientes-compare-split">
              <div>
                <h4 className="expedientes-compare-section-label">{patientA.name}</h4>
                <BeckAffectiveSpectrumChart spectrum={beckA} />
              </div>
              <div>
                <h4 className="expedientes-compare-section-label">{patientB.name}</h4>
                <BeckAffectiveSpectrumChart spectrum={beckB} />
              </div>
            </div>
          </section>

          <section className="expedientes-dashboard-block">
            <header className="expedientes-dashboard-header">
              <h3 className="expedientes-dashboard-title">
                Desglose cualitativo de preguntas críticas
              </h3>
            </header>
            <div className="expedientes-compare-split">
              <div>
                <h4 className="expedientes-compare-section-label">{patientA.name}</h4>
                <CriticalItemsBreakdownChart breakdown={itemsA} />
              </div>
              <div>
                <h4 className="expedientes-compare-section-label">{patientB.name}</h4>
                <CriticalItemsBreakdownChart breakdown={itemsB} />
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
